import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import MetaTags from '../components/common/MetaTags'
import ResumeUploadCard from '../components/common/ResumeUploadCard'
import WheelLoader from '../components/common/WheelLoader'
import { useProfileContext } from '../components/layout/AuthenticatedLayout'
import { pagesService } from '../services/pagesService'
import type { PageListItem } from '../types'

const MAX_PAGES = 5

export default function PagesPage() {
  const { profile } = useProfileContext()
  const [pages, setPages] = useState<PageListItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [deletingId, setDeletingId] = useState<number | null>(null)

  useEffect(() => {
    const fetchPages = async () => {
      try {
        const data = await pagesService.getAllPages()
        setPages(data)
      } catch {
        setError('Failed to load pages')
      } finally {
        setLoading(false)
      }
    }
    fetchPages()
  }, [])

  const handleDelete = async (id: number) => {
    if (!confirm('Are you sure you want to delete this page? This cannot be undone.')) return

    setDeletingId(id)
    try {
      await pagesService.deletePage(id)
      setPages(pages.filter((p) => p.id !== id))
    } catch {
      setError('Failed to delete page')
    } finally {
      setDeletingId(null)
    }
  }

  const handleToggleNav = async (page: PageListItem) => {
    try {
      const updated = await pagesService.updatePage(page.id, { show_in_nav: !page.show_in_nav })
      setPages(
        pages.map((p) => (p.id === page.id ? { ...p, show_in_nav: updated.show_in_nav } : p))
      )
    } catch {
      setError('Failed to update page')
    }
  }

  const canCreateMore = pages.length < MAX_PAGES

  if (!profile) {
    return (
      <div className="max-w-3xl mx-auto">
        <MetaTags title="Pages | profile.work" description="Manage your custom pages" />

        <div className="mb-8">
          <h1
            className="text-2xl sm:text-3xl text-black mb-2"
            style={{ fontFamily: "'Instrument Serif', serif" }}
          >
            Pages
          </h1>
          <p className="text-black/60">Create custom pages for your profile.</p>
        </div>

        <div className="bg-white/70 backdrop-blur-xl rounded-2xl shadow-xl border border-black/10 p-6">
          <ResumeUploadCard
            title="No profile yet"
            subtitle="Upload your resume to create your professional profile first."
          />
        </div>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto">
        <MetaTags title="Pages | profile.work" description="Manage your custom pages" />
        <div className="text-center py-12">
          <div className="mx-auto mb-4">
            <WheelLoader width={80} />
          </div>
          <p className="text-black/60">Loading pages...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-3xl mx-auto">
      <MetaTags title="Pages | profile.work" description="Manage your custom pages" />

      <div className="flex justify-between items-center mb-8">
        <div>
          <h1
            className="text-2xl sm:text-3xl text-black mb-2"
            style={{ fontFamily: "'Instrument Serif', serif" }}
          >
            Pages
          </h1>
          <p className="text-black/60">
            Create custom pages for your profile.{' '}
            <span className="text-black/40">
              ({pages.length}/{MAX_PAGES})
            </span>
          </p>
        </div>
        {canCreateMore ? (
          <Link
            to="/pages/new"
            className="px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl hover:from-indigo-700 hover:to-purple-700 transition-all shadow-sm"
          >
            New Page
          </Link>
        ) : (
          <span className="px-4 py-2 text-sm font-medium text-black/40 bg-black/5 rounded-xl cursor-not-allowed">
            Max pages reached
          </span>
        )}
      </div>

      {error && (
        <div className="p-3 rounded-xl bg-red-50 border border-red-200 mb-6">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {pages.length === 0 ? (
        <div className="bg-white/70 backdrop-blur-xl rounded-2xl shadow-xl border border-black/10 p-12 text-center">
          <svg
            className="w-12 h-12 mx-auto text-black/20 mb-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
          <h3 className="text-lg font-semibold text-black mb-2">No pages yet</h3>
          <p className="text-black/60 mb-6">
            Create custom pages like About, Services, or Portfolio.
          </p>
          <Link
            to="/pages/new"
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl hover:from-indigo-700 hover:to-purple-700 transition-all"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 4v16m8-8H4"
              />
            </svg>
            Create your first page
          </Link>
        </div>
      ) : (
        <div className="space-y-4">
          {pages.map((page) => (
            <div
              key={page.id}
              className="bg-white/70 backdrop-blur-xl rounded-2xl shadow-xl border border-black/10 overflow-hidden hover:shadow-2xl transition-shadow"
            >
              <div className="p-5">
                <div className="flex items-center gap-2 mb-2">
                  <span
                    className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                      page.status === 'published'
                        ? 'bg-green-100 text-green-700'
                        : 'bg-yellow-100 text-yellow-700'
                    }`}
                  >
                    {page.status === 'published' ? 'Published' : 'Draft'}
                  </span>
                  {page.show_in_nav && (
                    <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-700">
                      In Nav
                    </span>
                  )}
                </div>
                <h2 className="text-lg font-semibold text-black mb-1 line-clamp-1">
                  {page.icon && <span className="mr-2">{page.icon}</span>}
                  {page.title}
                </h2>
                <p className="text-sm text-black/40 mb-1">/pages/{page.slug}</p>
                {page.meta_description && (
                  <p className="text-sm text-black/60 line-clamp-2">{page.meta_description}</p>
                )}
                <div className="flex items-center gap-3 mt-4">
                  <Link
                    to={`/pages/edit/${page.id}`}
                    className="text-sm text-indigo-600 hover:text-indigo-700 font-medium"
                  >
                    Edit
                  </Link>
                  <button
                    onClick={() => handleToggleNav(page)}
                    className="text-sm text-black/60 hover:text-black font-medium"
                  >
                    {page.show_in_nav ? 'Hide from Nav' : 'Show in Nav'}
                  </button>
                  <button
                    onClick={() => handleDelete(page.id)}
                    disabled={deletingId === page.id}
                    className="text-sm text-red-500 hover:text-red-700 font-medium disabled:opacity-50"
                  >
                    {deletingId === page.id ? 'Deleting...' : 'Delete'}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
